import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import styled from "styled-components";
import { Loader } from "lucide-react";
import { PageHeader } from "../../../../components/page-header";
import PickupNoticeModal from "../../../../components/pickup-notice-modal";
import { useLanguage } from "../../../../contexts/language";
import { useNotification } from "../../../../contexts/notification";
import { useOrder } from "../../../../contexts/order";
import { useRoom } from "../../../../contexts/room";
import { saveOrderToLocalHistory } from "../../../../util";
import { getKitchenStatus } from "../fnb/mock";

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
`;

const EmptyStateContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateContent = styled.div`
  text-align: center;
`;

const EmptyStateText = styled.p`
  color: #4b5563;
  margin-bottom: 16px;
`;

const EmptyStateButton = styled.button`
  padding: 8px 16px;
  background-color: #c9a961;
  color: #fff;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(201, 169, 97, 0.9);
  }
`;

const MainContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
  padding: 32px 16px;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 24px;
  background-color: #fff;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const OrderItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 256px;
  overflow-y: auto;
`;

const OrderItemThumbnail = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const OrderItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
`;

const OrderItemName = styled.span`
  color: #4b5563;
`;

const OrderItemPrice = styled.span`
  color: #333;
  font-weight: 500;
`;

const ItemNoteInput = styled.textarea`
  margin-top: 6px;
  width: 100%;
  min-height: 52px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #555;
  resize: vertical;
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentMethodLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  input[type="radio"] {
    width: 16px;
    height: 16px;
  }

  span {
    color: #333;
    font-weight: 500;
  }
`;

const CardForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormField = styled.div``;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 16px;
  background-color: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #c9a961;
    box-shadow: 0 0 0 2px rgba(201, 169, 97, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const RightColumn = styled.div``;

const StickyCard = styled(Card)`
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SummaryLabel = styled.span`
  color: #4b5563;
`;

const SummaryValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: #c9a961;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #c9a961;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: rgba(201, 169, 97, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const CancelButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid #e5e5e5;
  color: #333;
  background-color: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VoucherRow = styled.div`
  display: flex;
  gap: 8px;
`;

const VoucherCodeInput = styled(FormInput)`
  text-transform: uppercase;

  &::placeholder {
    text-transform: none;
  }
`;

const VoucherApplyButton = styled.button`
  padding: 8px 16px;
  background-color: #c9a961;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VoucherError = styled.p`
  color: #dc2626;
  font-size: 13px;
  margin: 8px 0 0 0;
`;

const VoucherApplied = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #15803d;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
`;

const DiscountValue = styled.span`
  color: #15803d;
  font-weight: 500;
`;

const AutoAppliedToast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: calc(100% - 32px);
  background-color: #15803d;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 20px;
  border-radius: 999px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
  animation: toastIn 0.25s ease-out;

  @keyframes toastIn {
    from {
      opacity: 0;
      transform: translate(-50%, -12px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
`;

export default function CheckoutPage(props) {
  const { building_code } = props;
  const { t } = useLanguage();
  const { subscribe, getSubscriptionId } = useNotification();
  const {
    cart,
    getCartTotal,
    processPayment,
    isProcessing,
    updateCartItemNote,
    voucherCouponId,
    setVoucherCouponId,
  } = useOrder();
  const { roomNumber, guestName } = useRoom();
  const [userName, setUserName] = useState(guestName || "");
  const [userEmail, setUserEmail] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [voucherError, setVoucherError] = useState("");
  const [isCheckingVoucher, setIsCheckingVoucher] = useState(false);
  const [autoApplied, setAutoApplied] = useState(false);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState(null);

  // Deep-link voucher (?vca=<coupon_id> from the FNB page): auto-apply by opaque id so the guest
  // never sees/types the code. Defined before the early return to keep hook order stable. The
  // "applied" ref is set on SUCCESS (not before the fetch), so React StrictMode's dev double-mount
  // can't leave it stuck after aborting the first attempt. Manual field below still works too.
  const autoVoucherAppliedRef = useRef(false);
  useEffect(() => {
    if (
      !voucherCouponId ||
      autoVoucherAppliedRef.current ||
      voucher ||
      cart.length === 0
    ) {
      return undefined;
    }
    const controller = new AbortController();
    const lines = cart.map((item) => ({
      orderable_type: item.orderableType || "FnbMenuItem",
      orderable_id: item.id,
      quantity: item.quantity,
    }));
    fetch("/api/v1/coupons/preview", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ coupon_id: voucherCouponId, lines }),
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload) return;
        autoVoucherAppliedRef.current = true;
        setVoucher(payload.data);
        setVoucherCode(payload.data.code || "");
        setAutoApplied(true);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [voucherCouponId, cart, voucher]);

  // Auto-dismiss the "voucher applied" flash.
  useEffect(() => {
    if (!autoApplied) return undefined;
    const timer = setTimeout(() => setAutoApplied(false), 5000);
    return () => clearTimeout(timer);
  }, [autoApplied]);

  /* Rendered from BOTH returns below, because processPayment CLEARS the cart on success: by the time
   * the interstitial is asked for, this component has already fallen through to "your cart is empty".
   * Mounting it only in the main return meant it never appeared at all.
   */
  const pickupNotice = pendingPaymentUrl ? (
    <PickupNoticeModal
      onDone={() => window.location.assign(pendingPaymentUrl)}
    />
  ) : null;

  if (cart.length === 0) {
    return (
      <EmptyStateContainer>
        {pickupNotice}
        <EmptyStateContent>
          <EmptyStateText>{t.order.cart.empty}</EmptyStateText>
          <EmptyStateButton onClick={() => window.history.back()}>
            {t.order.backButton}
          </EmptyStateButton>
        </EmptyStateContent>
      </EmptyStateContainer>
    );
  }

  const TAX_RATE = 0;
  const subtotal = getCartTotal();
  const tax = subtotal * TAX_RATE;
  const discount = voucher ? Number(voucher.discount_rupiah || 0) : 0;
  const total = Math.max(subtotal + tax - discount, 0);
  const formatAmount = (amount) =>
    `Rp${Math.round(Number(amount || 0)).toLocaleString("id-ID")}`;

  const buildVoucherLines = () =>
    cart.map((item) => ({
      orderable_type: item.orderableType || "FnbMenuItem",
      orderable_id: item.id,
      quantity: item.quantity,
    }));

  const previewVoucher = async (identifier) => {
    setIsCheckingVoucher(true);
    setVoucherError("");
    try {
      const response = await fetch("/api/v1/coupons/preview", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ...identifier,
          lines: buildVoucherLines(),
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setVoucher(null);
        setVoucherError(payload?.message || t.order.voucher.invalid);
        return;
      }
      setVoucher(payload.data);
      setVoucherCode(payload.data.code || "");
    } catch {
      setVoucher(null);
      setVoucherError(t.order.voucher.invalid);
    } finally {
      setIsCheckingVoucher(false);
    }
  };

  const applyVoucher = () => {
    const code = voucherCode.trim();
    if (code) previewVoucher({ voucher_code: code });
  };

  const removeVoucher = () => {
    setVoucher(null);
    setVoucherCode("");
    setVoucherError("");
    setAutoApplied(false);
    autoVoucherAppliedRef.current = true;
    setVoucherCouponId(null);
  };

  const handlePayment = async () => {
    try {
      const kitchenStatus = await getKitchenStatus();
      if (kitchenStatus && !kitchenStatus.is_open) {
        window.alert(t.kitchen.closedMessage);
        return;
      }
      const webPushSubscriptionId = getSubscriptionId() ?? (await subscribe());
      const order = await processPayment({
        userName,
        buildingCode: building_code,
        roomNumber: roomNumber || undefined,
        webPushSubscriptionId: webPushSubscriptionId || undefined,
        email: userEmail || undefined,
        voucherCode: voucher ? voucher.code : undefined,
      });
      if (order?.id) {
        saveOrderToLocalHistory({ buildingCode: building_code, order });
        // Straight to Xendit after the notice rather than via the order page's auto_pay hop: the
        // payment link is already in hand here, and Orders::CreateService points both Xendit return
        // URLs at the order page anyway, so the guest lands there either way. The auto_pay effect
        // stays on that page as the re-entry path for an unpaid order.
        if (order.payment_link_url) {
          setPendingPaymentUrl(order.payment_link_url);
        } else {
          router.visit(
            `/hebe/${building_code}/hgs/orders/${order.id}?auto_pay=1`,
          );
        }
      }
    } catch (error) {
      window.alert(error?.message || "Failed to create order");
    }
  };

  return (
    <PageContainer>
      {pickupNotice}
      {autoApplied && (
        <AutoAppliedToast>{t.order.voucher.autoApplied}</AutoAppliedToast>
      )}
      <PageHeader title={t.order.payment.title} maxWidth="768px" />

      <MainContent>
        <ContentGrid>
          <LeftColumn>
            <Card>
              <CardTitle>{t.order.payment.orderSummary}</CardTitle>
              <OrderItemsContainer>
                {cart.map((item) => (
                  <OrderItem key={item.id}>
                    {item.image && (
                      <OrderItemThumbnail src={item.image} alt={item.name} />
                    )}
                    <div style={{ flex: 1 }}>
                      <OrderItemName>
                        {item.name} x {item.quantity}
                      </OrderItemName>
                      <ItemNoteInput
                        value={item.note || ""}
                        onChange={(event) =>
                          updateCartItemNote(item.id, event.target.value)
                        }
                        maxLength={200}
                        placeholder={t.order.itemNotePlaceholder}
                      />
                    </div>
                    <OrderItemPrice>
                      {formatAmount(
                        Number(item?.price?.amount || 0) * item.quantity,
                      )}
                    </OrderItemPrice>
                  </OrderItem>
                ))}
              </OrderItemsContainer>
            </Card>

            <Card>
              <CardTitle>{t.order.payment.orderDetails}</CardTitle>
              <CardForm>
                <FormField>
                  <FormLabel>{t.order.payment.yourName}</FormLabel>
                  <FormInput
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder={t.order.payment.enterYourName}
                  />
                </FormField>
                <FormField>
                  <FormLabel>{t.order.payment.emailOptional}</FormLabel>
                  <FormInput
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="you@email.com"
                  />
                </FormField>
              </CardForm>
            </Card>

            <Card>
              <CardTitle>{t.order.voucher.title}</CardTitle>
              <VoucherRow>
                <VoucherCodeInput
                  type="text"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                  placeholder={t.order.voucher.placeholder}
                  disabled={Boolean(voucher)}
                />
                {voucher ? (
                  <VoucherApplyButton type="button" onClick={removeVoucher}>
                    {t.order.voucher.remove}
                  </VoucherApplyButton>
                ) : (
                  <VoucherApplyButton
                    type="button"
                    onClick={applyVoucher}
                    disabled={isCheckingVoucher || !voucherCode.trim()}
                  >
                    {isCheckingVoucher
                      ? t.order.voucher.checking
                      : t.order.voucher.apply}
                  </VoucherApplyButton>
                )}
              </VoucherRow>
              {voucherError && <VoucherError>{voucherError}</VoucherError>}
              {voucher && (
                <VoucherApplied>
                  <span>
                    {t.order.voucher.applied}: {voucher.code}
                  </span>
                  <span>-{formatAmount(discount)}</span>
                </VoucherApplied>
              )}
            </Card>
          </LeftColumn>

          <RightColumn>
            <StickyCard>
              <CardTitle>{t.order.cart.total}</CardTitle>
              <SummarySection>
                <SummaryRow>
                  <SummaryLabel>{t.order.cart.subtotal}</SummaryLabel>
                  <SummaryValue>{formatAmount(subtotal)}</SummaryValue>
                </SummaryRow>
                <SummaryRow>
                  <SummaryLabel>{t.order.cart.tax}</SummaryLabel>
                  <SummaryValue>{formatAmount(tax)}</SummaryValue>
                </SummaryRow>
                {voucher && (
                  <SummaryRow>
                    <SummaryLabel>{t.order.voucher.discount}</SummaryLabel>
                    <DiscountValue>-{formatAmount(discount)}</DiscountValue>
                  </SummaryRow>
                )}
              </SummarySection>
              <TotalRow>
                <TotalLabel>{t.order.cart.total}</TotalLabel>
                <TotalValue>{formatAmount(total)}</TotalValue>
              </TotalRow>

              <ConfirmButton onClick={handlePayment} disabled={isProcessing}>
                {isProcessing && <Loader size={16} />}
                {isProcessing
                  ? t.order.payment.processing
                  : t.order.payment.confirm}
              </ConfirmButton>

              <CancelButton
                onClick={() => window.history.back()}
                disabled={isProcessing}
              >
                {t.order.payment.cancel}
              </CancelButton>
            </StickyCard>
          </RightColumn>
        </ContentGrid>
      </MainContent>
    </PageContainer>
  );
}
